/**
 * LanguageSwitcher — compact icon-based popup for selecting display language.
 *
 * Renders a 48x48 globe button. Clicking opens a popup menu listing the
 * supported languages; selecting one calls i18next.changeLanguage(), stores
 * the choice in localStorage, and invokes the optional onLanguageChange
 * callback (used by consumers to sync with user preferences on the server).
 *
 * Popup closes on outside click and on Escape. ARIA attributes follow the
 * authoring practices pattern for a button-triggered menu.
 *
 * @module shared/i18n/LanguageSwitcher
 */

import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  LANGUAGE_STORAGE_KEY,
} from './i18n-config';

interface LanguageSwitcherProps {
  /** Callback to persist language preference to user account (if authenticated). */
  onLanguageChange?: (languageCode: string) => void;
}

/**
 * Compact globe-icon language switcher with popup menu.
 *
 * Designed for placement in headers and footers. The button has a 48x48
 * minimum touch target (WCAG 2.5.8) and exposes standard menu ARIA roles.
 */
export function LanguageSwitcher({
  onLanguageChange,
}: LanguageSwitcherProps): React.JSX.Element {
  const { i18n, t } = useTranslation();
  const menuId = `ipa-language-menu-${useId()}`;

  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function handlePointerDown(event: MouseEvent): void {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [open]);

  const selectLanguage = useCallback(
    (languageCode: string): void => {
      void i18n.changeLanguage(languageCode);
      try {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, languageCode);
      } catch {
        // localStorage may be unavailable (Safari private mode, etc.).
      }
      onLanguageChange?.(languageCode);
      setOpen(false);
      buttonRef.current?.focus();
    },
    [i18n, onLanguageChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation();
        setOpen(false);
        buttonRef.current?.focus();
      }
    },
    [open],
  );

  const ariaLabel = t('common:footer.selectLanguage', { defaultValue: 'Change language' });

  return (
    <div
      ref={containerRef}
      data-testid="language-switcher"
      onKeyDown={handleKeyDown}
      style={{ position: 'relative', display: 'inline-block' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-label={ariaLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        data-testid="language-switcher-button"
        style={buttonStyle}
      >
        <span aria-hidden="true" style={{ fontSize: '1.25rem', lineHeight: 1 }}>
          {'\uD83C\uDF10'}
        </span>
      </button>

      {open && (
        <ul
          id={menuId}
          role="menu"
          data-testid="language-switcher-menu"
          style={menuStyle}
        >
          {SUPPORTED_LANGUAGES.map((lang) => {
            const isSelected = i18n.language === lang.code;
            return (
              <li key={lang.code} role="none" style={{ margin: 0 }}>
                <button
                  type="button"
                  role="menuitem"
                  aria-current={isSelected ? 'true' : undefined}
                  onClick={() => selectLanguage(lang.code)}
                  data-testid={`language-option-${lang.code}`}
                  style={{
                    ...menuItemStyle,
                    fontWeight: isSelected ? 600 : 400,
                    background: isSelected ? '#eff6ff' : 'transparent',
                  }}
                >
                  <span>{lang.label}</span>
                  {isSelected && (
                    <span aria-hidden="true" style={{ color: '#2563eb' }}>&#10003;</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

const buttonStyle: React.CSSProperties = {
  minWidth: '48px',
  minHeight: '48px',
  padding: '0.25rem',
  border: '1px solid transparent',
  borderRadius: '9999px',
  background: 'transparent',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const menuStyle: React.CSSProperties = {
  position: 'absolute',
  top: 'calc(100% + 0.25rem)',
  right: 0,
  listStyle: 'none',
  margin: 0,
  padding: '0.25rem',
  background: '#ffffff',
  border: '1px solid #e5e7eb',
  borderRadius: '0.5rem',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  minWidth: '10rem',
  zIndex: 1000,
};

const menuItemStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: '0.75rem',
  width: '100%',
  minHeight: '48px',
  padding: '0.5rem 0.75rem',
  border: 'none',
  borderRadius: '0.375rem',
  fontSize: '0.875rem',
  textAlign: 'left',
  cursor: 'pointer',
  color: '#111827',
};
